// src/components/agent/session/turnReducer.js
//
// Pure SSE-event → overlay reducer (frontend spec §8.4, event table §4.2).
// Builds the same ViewPart shapes as partFromDoc, preserving v1's
// accumulation logic. Unit-testable without React.
//
// Overlay shape: { id, parts, live: { thinking, thinkingText, compacting,
// streaming }, retired, retiredBy, done, leaseLost }
//
// `approval_required` is deliberately NOT handled here. An approval is durable
// state, not stream state: the session hook hydrates the record (live event and
// Firestore snapshot alike) through session/approvalHydration.js and attaches
// one deduplicated card per approval id to the message it belongs to.

export function turnReducer(overlay, eventData) {
  if (!overlay || !eventData || typeof eventData !== 'object') return overlay;

  switch (eventData.type) {
    case 'stream_start':
      // Adopt the real Firestore doc id; clear compacting — the response is starting.
      return {
        ...overlay,
        id: eventData.agent_message_id,
        live: { ...overlay.live, compacting: false },
      };

    case 'compacting':
      // Mutates only overlay.live — never parts (§8.4). A compaction at a
      // cycle boundary arrives after stream_start and the wire has no
      // "compaction finished" frame, so the flag is cleared by the first
      // event that proves the model is producing output again: a thinking
      // block, a text delta, or a tool call (B9-6).
      return { ...overlay, live: { ...overlay.live, compacting: true } };

    case 'thinking_start':
      return { ...overlay, live: { ...overlay.live, thinking: true, thinkingText: '', compacting: false } };

    case 'thinking_delta':
      return {
        ...overlay,
        live: { ...overlay.live, thinkingText: (overlay.live?.thinkingText || '') + (eventData.thinking || '') },
      };

    case 'thinking_complete':
      return { ...overlay, live: { ...overlay.live, thinking: false, thinkingText: '' } };

    case 'content': {
      // Extend the trailing text part or open a new one — the wire has no
      // text-block boundary, so all adjacent text accumulates into one part.
      const parts = [...overlay.parts];
      const lastPart = parts.length > 0 ? parts[parts.length - 1] : null;
      if (lastPart && lastPart.kind === 'text') {
        parts[parts.length - 1] = { ...lastPart, text: lastPart.text + eventData.content };
      } else {
        parts.push({ kind: 'text', text: eventData.content });
      }
      return { ...overlay, parts, live: { ...overlay.live, compacting: false } };
    }

    case 'tool_start':
      return {
        ...overlay,
        parts: [
          ...overlay.parts,
          {
            kind: 'tool_call',
            toolUseId: eventData.tool_use_id,
            name: eventData.tool,
            statusMessage: eventData.status_message || `Starting ${eventData.tool}...`,
            isError: false,
            denied: false,
            completed: false,
            executing: false,
          },
        ],
        live: { ...overlay.live, compacting: false },
      };

    case 'tool_executing': {
      // Carries {tool, tool_use_id} and NO status text (backend §7.4). Mark
      // the matched part as actively executing; ignore if tool_use_id absent.
      if (!eventData.tool_use_id) return overlay;
      const parts = [...overlay.parts];
      const toolIndex = parts.findLastIndex(
        (p) => p.kind === 'tool_call' && p.toolUseId === eventData.tool_use_id
      );
      if (toolIndex === -1) return overlay;
      parts[toolIndex] = { ...parts[toolIndex], executing: true };
      return { ...overlay, parts };
    }

    case 'tool_complete':
    case 'code_execution': {
      // Match by tool_use_id (v1's find-last, with its find-last-incomplete
      // fallback carried for backward compat).
      const parts = [...overlay.parts];
      let toolIndex = parts.findLastIndex(
        (p) => p.kind === 'tool_call' && p.toolUseId === eventData.tool_use_id
      );
      if (toolIndex === -1) {
        toolIndex = parts.findLastIndex((p) => p.kind === 'tool_call' && !p.completed);
      }
      if (toolIndex === -1) return overlay;
      parts[toolIndex] = {
        ...parts[toolIndex],
        completed: true,
        executing: false,
        // The live outcome must match what the ledger will hydrate later, so
        // a thrown failure or a refusal is never painted as a success while
        // the turn is still streaming.
        isError: !!eventData.is_error,
        denied: !!eventData.denied,
        statusMessage: eventData.status_message || `${parts[toolIndex].name} completed`,
      };
      return { ...overlay, parts };
    }

    // A chart or table frame names where its data is stored and never carries
    // the data itself, so a large table cannot overflow the stream. The block
    // fetches that stored file, exactly as it does for a reloaded chat.
    case 'charts_available':
      return {
        ...overlay,
        parts: [
          ...overlay.parts,
          { kind: 'charts', toolUseId: eventData.tool_use_id, gcsPath: eventData.charts_gcs_path },
        ],
      };

    case 'table_available':
      return {
        ...overlay,
        parts: [
          ...overlay.parts,
          { kind: 'table', toolUseId: eventData.tool_use_id, gcsPath: eventData.table_gcs_path },
        ],
      };

    case 'file_available':
      return {
        ...overlay,
        parts: [
          ...overlay.parts,
          {
            kind: 'file',
            toolUseId: eventData.tool_use_id,
            filename: eventData.filename,
            description: eventData.description,
            downloadUrl: eventData.download_url,
            gcsPath: null,
            sandboxPath: null,
          },
        ],
      };

    case 'done':
      // The overlay is NOT cleared at done (§7.3) — mark it finishing and let
      // render-source selection retire it against the terminal doc.
      return {
        ...overlay,
        done: true,
        live: { ...overlay.live, streaming: false, thinking: false, compacting: false },
      };

    case 'lease_lost':
      // The turn behind this stream no longer owns the chat and has stopped
      // without committing anything. It is deliberately not `done`: `done`
      // means the finished message is moments away, and here it is not. The
      // text already streamed is kept and shown as an interrupted response.
      return {
        ...overlay,
        leaseLost: true,
        live: { ...overlay.live, streaming: false, thinking: false, compacting: false },
      };

    default:
      // Unknown event types (and non-part events like canvas_file, handled
      // elsewhere) must never break the reducer.
      return overlay;
  }
}
